import { DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, combineLatest, distinctUntilChanged, EMPTY, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';

import { PlatformPostInsightResponseDto } from '@malou-io/package-dto';
import { getPlatformDefinition, isNotNil, MalouComparisonPeriod, PlatformFilterPage, PlatformKey } from '@malou-io/package-utils';

import { PostInsightsV2Service } from '#core/services/post-insights-v2.service';
import { StoriesApiService } from '#modules/posts/services/stories.api.service';
import { Restaurant } from '#modules/restaurants/models/restaurant';
import { RestaurantStore } from '#modules/restaurants/stores/restaurant.store';
import { PlatformSocialNetworkPostInsight } from '#modules/statistics-social-networks/models/social-network-post-insight';
import { StatisticsFiltersStore } from '#modules/statistics/stores/store/statistics-filters-store/models/statistics-filters-store.model';
import { StatisticsStore } from '#modules/statistics/stores/store/statistics.store';
import { ViewBy } from '#shared/enums/view-by.enum';
import { getChartViewByFromDates, isDateSetOrGenericPeriod } from '#shared/helpers/index';
import { DatesAndPeriod } from '#shared/models/index';
import { EnumTranslatePipe } from '#shared/pipes/enum-translate.pipe';

@Injectable()
export class SocialNetworksPostsInsightsV2Context {
    private readonly _restaurantStore = inject(RestaurantStore);
    private readonly _enumTranslatePipe = inject(EnumTranslatePipe);
    private readonly _storiesService = inject(StoriesApiService);
    private readonly _postInsightsV2Service = inject(PostInsightsV2Service);
    private readonly _statisticsStore = inject(StatisticsStore);
    private readonly _destroyRef = inject(DestroyRef);

    readonly statisticsFilters$ = toObservable(this._statisticsStore.filters);
    readonly selectedRestaurant$ = toObservable(this._restaurantStore.restaurant).pipe(
        distinctUntilChanged((prev, curr) => prev?._id === curr?._id)
    );

    readonly isLoading = signal(true);
    readonly httpError: WritableSignal<string | null> = signal(null);
    readonly areAllPlatformsInError: WritableSignal<boolean> = signal(false);
    readonly platformsErrorTooltip: WritableSignal<string | null> = signal(null);
    readonly hasDate: WritableSignal<boolean> = signal(false);
    readonly comparisonPeriod: WritableSignal<MalouComparisonPeriod> = signal(MalouComparisonPeriod.PREVIOUS_PERIOD);
    readonly currentPeriodDates: WritableSignal<{ startDate: Date; endDate: Date } | null> = signal(null);
    readonly previousPeriodDates: WritableSignal<{ startDate: Date; endDate: Date } | null> = signal(null);

    readonly viewBy: WritableSignal<ViewBy | null> = signal(null);

    readonly currentSocialNetworkPostInsights: WritableSignal<PlatformSocialNetworkPostInsight[]> = signal([]);
    readonly previousSocialNetworkPostInsights: WritableSignal<PlatformSocialNetworkPostInsight[]> = signal([]);

    readonly storiesCount: WritableSignal<number> = signal(0);
    readonly hasSelectedPlatformWithStories: WritableSignal<boolean> = signal(true);

    readonly postsWithInsights$ = combineLatest([this.selectedRestaurant$, this.statisticsFilters$]).pipe(
        filter(
            ([restaurant, statisticsFilters]: [Restaurant, StatisticsFiltersStore]) =>
                !!restaurant &&
                isDateSetOrGenericPeriod(statisticsFilters.dates) &&
                isNotNil(statisticsFilters.dates.startDate) &&
                isNotNil(statisticsFilters.dates.endDate)
        ),
        map(([restaurant, statisticsFilters]: [Restaurant, StatisticsFiltersStore]) => [
            restaurant,
            {
                startDate: statisticsFilters.dates.startDate,
                endDate: statisticsFilters.dates.endDate,
            },
            statisticsFilters.platforms[PlatformFilterPage.SOCIAL_NETWORKS],
            statisticsFilters.comparisonPeriod,
        ]),
        tap(() => this._reset()),
        switchMap(
            ([restaurant, dates, platformKeys, comparisonPeriod]: [Restaurant, DatesAndPeriod, PlatformKey[], MalouComparisonPeriod]) => {
                const startDate = dates.startDate as Date;
                const endDate = dates.endDate as Date;
                const { _id: restaurantId } = restaurant;

                const hasSelectedPlatforms = platformKeys.length > 0;
                const hasSelectedPlatformWithStories = this._doesAnySelectedPlatformSupportStories(platformKeys);
                this.hasSelectedPlatformWithStories.set(hasSelectedPlatformWithStories);

                return forkJoin([
                    hasSelectedPlatforms
                        ? this._postInsightsV2Service.getRestaurantPostInsights$({
                              restaurantId,
                              startDate,
                              endDate,
                              platformKeys,
                          })
                        : of([]),
                    hasSelectedPlatforms
                        ? this._postInsightsV2Service.getRestaurantPostInsights$({
                              restaurantId,
                              startDate,
                              endDate,
                              platformKeys,
                              comparisonPeriod,
                          })
                        : of([]),
                    hasSelectedPlatformWithStories
                        ? this._storiesService.getPublishedStoriesCount$({ restaurantId, startDate, endDate, platformKeys })
                        : of(0),
                    of(comparisonPeriod),
                ]).pipe(
                    // Caught here rather than on the outer pipe, which would end the stream and ignore every later filter change.
                    catchError((error) => {
                        this.httpError.set(error);
                        this.hasDate.set(false);
                        this.isLoading.set(false);
                        return EMPTY;
                    })
                );
            }
        ),
        map(
            ([currentPlatformPostInsights, previousPlatformPostInsights, storiesCount, comparisonPeriod]: [
                PlatformPostInsightResponseDto[],
                PlatformPostInsightResponseDto[],
                number,
                MalouComparisonPeriod,
            ]) => {
                this.isLoading.set(false);
                this.storiesCount.set(storiesCount);

                this.currentSocialNetworkPostInsights.set(
                    currentPlatformPostInsights.map((dto) => PlatformSocialNetworkPostInsight.fromPlatformPostInsightResponseDto(dto))
                );

                this._setPostWithInsightsV2InStore(this.currentSocialNetworkPostInsights());

                this.previousSocialNetworkPostInsights.set(
                    previousPlatformPostInsights.map((dto) => PlatformSocialNetworkPostInsight.fromPlatformPostInsightResponseDto(dto))
                );
                this.areAllPlatformsInError.set(this._areAllPlatformsInError(currentPlatformPostInsights));
                const platformsInError = this._getPlatformsInError(currentPlatformPostInsights);
                if (platformsInError.length) {
                    this.platformsErrorTooltip.set(this._getPlatformsErrorTooltip(platformsInError));
                }

                // The API returns one entry per requested platform, so an empty list means no social network is selected and there is no period to read.
                if (!currentPlatformPostInsights.length || !previousPlatformPostInsights.length) {
                    this.hasDate.set(false);
                    return;
                }

                const { startDate, endDate } = currentPlatformPostInsights[0].period;
                const { startDate: previousStartDate, endDate: previousEndDate } = previousPlatformPostInsights[0].period;

                this.viewBy.set(getChartViewByFromDates(new Date(startDate), new Date(endDate)));

                this.currentPeriodDates.set({ startDate: new Date(startDate), endDate: new Date(endDate) });
                this.previousPeriodDates.set({ startDate: new Date(previousStartDate), endDate: new Date(previousEndDate) });
                this.comparisonPeriod.set(comparisonPeriod);

                this.hasDate.set(!this.areAllPlatformsInError());
            }
        ),
        takeUntilDestroyed(this._destroyRef)
    );

    private _setPostWithInsightsV2InStore(data: PlatformSocialNetworkPostInsight[]): void {
        const postInsights = data.flatMap((platformPosts) => platformPosts.postInsights);
        this._statisticsStore.editPostWithInsightsV2RawData(postInsights);
    }

    private _doesAnySelectedPlatformSupportStories(platformKeys: PlatformKey[]): boolean {
        return platformKeys.some((platformKey) => {
            const platformDefinition = getPlatformDefinition(platformKey);
            return platformDefinition?.hasStory;
        });
    }

    private _reset(): void {
        this.httpError.set(null);
        this.isLoading.set(true);
        this.platformsErrorTooltip.set(null);
        this.areAllPlatformsInError.set(false);
    }

    private _areAllPlatformsInError(currentPlatformPostInsights: PlatformPostInsightResponseDto[]): boolean {
        return currentPlatformPostInsights.every((platformPostInsight) => !!platformPostInsight.error);
    }

    private _getPlatformsInError(currentPlatformPostInsights: PlatformPostInsightResponseDto[]): PlatformKey[] {
        return currentPlatformPostInsights
            .filter((platformPostInsight) => !!platformPostInsight.error)
            .map((platformPostInsight) => platformPostInsight.platformKey);
    }

    private _getPlatformsErrorTooltip(platformsInError: PlatformKey[]): string {
        return platformsInError.map((platform) => this._enumTranslatePipe.transform(platform, 'platform_key')).join(', ');
    }
}
